import React, { useEffect, useState } from 'react'
import { FlatList, useWindowDimensions } from 'react-native'
import { useTheme } from 'styled-components/native'
import MaterialIcon from 'react-native-vector-icons/MaterialIcons'
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons'

import i18n from 'APP/I18n'
import Shimmer from 'APP/Components/Shimmer'
import { StyledButton, StyledLoadingView, StyledSearchWrapper, StyledSearchText } from './styles'
import Analytics from 'APP/Services/Analytics'

interface SearchErrorProps {
  onRetry: () => void
  onClose: () => void
}

export const SearchError = ({ onRetry, onClose }: SearchErrorProps) => {
  const theme = useTheme()
  const [retryDisabled, setRetryDisabled] = useState(true)

  useEffect(() => {
    const timer = setTimeout(() => setRetryDisabled(false), 3000)
    return () => clearTimeout(timer)
  }, [])

  return (
    <StyledSearchWrapper testID="SEMANTIC_SEARCH_ERROR">
      <MaterialCommunityIcons
        name="magnify-remove-outline"
        size={theme.metrics.icon.medium}
        color={theme.colors.disabled}
      />
      <StyledSearchText variant="bodyNormal">
        {i18n.t('SemanticSearch.loadingError')}
      </StyledSearchText>
      <StyledButton
        title={i18n.t('SemanticSearch.chooseFromListButton')}
        onPress={() => {
          Analytics.trackEvent('button_click', {
            button_value: 'Semantic Search - Choose From List',
          })
          onClose()
        }}
        variant="secondary"
        widthVariant="content"
        testID="CHOOSE_FROM_LIST_BUTTON"
      />
      <StyledButton
        title={i18n.t('SemanticSearch.retryButton')}
        disabled={retryDisabled}
        onPress={() => {
          Analytics.trackEvent('button_click', {
            button_value: 'Semantic Search - Retry',
          })
          onRetry()
        }}
        variant="tertiary"
        widthVariant="content"
        testID="RETRY_BUTTON"
      />
    </StyledSearchWrapper>
  )
}

export const LoadingSkeleton = () => {
  const theme = useTheme()
  const { width } = useWindowDimensions()

  return (
    <FlatList
      testID="LOADING_SEMANTIC_SEARCH_RESULTS"
      data={[0, 1, 2, 3, 4]}
      keyExtractor={(item) => item.toString()}
      renderItem={() => (
        <StyledLoadingView>
          <Shimmer height={theme.fonts.fontSize.regular} width={width * 0.1} />
          <Shimmer height={theme.fonts.fontSize.regular} width={width * 0.2} />
        </StyledLoadingView>
      )}
    />
  )
}

export const InitialPrompt = () => {
  const theme = useTheme()

  return (
    <StyledSearchWrapper testID="INITIAL_SEARCH_PROMPT">
      <MaterialIcon
        name="search"
        size={theme.metrics.icon.small}
        color={theme.colors.secondaryText}
      />
      <StyledSearchText variant="bodyNormal">
        {i18n.t('SemanticSearch.defaultResultsPrompt')}
      </StyledSearchText>
    </StyledSearchWrapper>
  )
}

interface NoResultsProps {
  onClose: () => void
}

export const NoResults = ({ onClose }: NoResultsProps) => {
  const theme = useTheme()

  return (
    <StyledSearchWrapper testID="NO_RESULTS_FROM_SEARCH">
      <MaterialIcon
        name="search-off"
        size={theme.metrics.icon.small}
        color={theme.colors.secondaryText}
      />
      <StyledSearchText variant="bodyNormal">
        {i18n.t('SemanticSearch.noResultsFromSearch')}
      </StyledSearchText>
      <StyledButton
        title={i18n.t('SemanticSearch.browseButton')}
        onPress={() => {
          Analytics.trackEvent('button_click', {
            button_value: 'Semantic Search - Browse',
          })
          onClose()
        }}
        variant="secondary"
        widthVariant="content"
        testID="BROWSE_BUTTON"
      />
    </StyledSearchWrapper>
  )
}
